/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { DynamicImage } from '@/components/dynamic-image';
import { cn } from '@/lib/utils';
import type { DynamicImageDimensions } from '@/lib/images/dynamic-image';
import { useGallerySelection } from '@/components/image-gallery/use-gallery-selection';
import type { GalleryImage } from '@/components/image-gallery/types';

const DEFAULT_WIDTHS_THUMBNAIL: DynamicImageDimensions = { base: 144, sm: 176, md: 240, lg: 168 };
// Preload the main-image variant a thumbnail targets (not the thumbnail's own small size) — same rationale as
// `ImageGallery`'s own thumbnail preloading.
const DEFAULT_PRELOAD_WIDTHS: DynamicImageDimensions = { base: '100vw', lg: '50vw', '2xl': 680 };

interface ImageGalleryThumbsProps {
    images: GalleryImage[];
    selectedImageIndex: number;
    onSelectImage: (index: number) => void;
    productName?: string;
    widths?: DynamicImageDimensions;
    /** Widths used to preload the main image a thumbnail targets, on hover/focus intent. */
    preloadWidths?: DynamicImageDimensions;
    className?: string;
}

/**
 * Standalone full-width thumbnail row, detached from `ImageGallery`'s main image. Used where a layout needs
 * the thumbnail strip to span content that the main image doesn't (e.g. the bundle PDP media block, where
 * thumbnails run full-width below both the CMS-content column and the gallery column). Pair with
 * `<ImageGallery hideThumbnails selectedImageIndex={index} onSelectImage={setIndex} />` so both share the
 * same controlled selection state.
 */
export default function ImageGalleryThumbs({
    images,
    selectedImageIndex,
    onSelectImage,
    productName,
    widths = DEFAULT_WIDTHS_THUMBNAIL,
    preloadWidths = DEFAULT_PRELOAD_WIDTHS,
    className,
}: ImageGalleryThumbsProps): ReactElement | null {
    const { t: tProduct } = useTranslation('product');
    const { handleThumbnailClick, handleThumbnailIntent } = useGallerySelection({
        images,
        onSelect: onSelectImage,
        preloadWidths,
    });

    if (images.length <= 1) {
        return null;
    }

    const imageAltFallback = productName || tProduct('imageAlt') || 'Product Image';

    return (
        <div className={cn('grid grid-cols-4 gap-2 sm:gap-3', className)}>
            {images.map((image, index) => (
                <button
                    key={image.src + (image.thumbSrc || '')}
                    type="button"
                    onClick={handleThumbnailClick}
                    onPointerEnter={handleThumbnailIntent}
                    onFocus={handleThumbnailIntent}
                    data-index={index}
                    className={cn(
                        'aspect-square overflow-hidden rounded-none bg-muted border-2 transition-colors cursor-pointer',
                        selectedImageIndex === index ? 'border-primary' : 'border-transparent hover:border-border'
                    )}>
                    <DynamicImage
                        src={image.thumbSrc || image.src}
                        alt={image.alt || imageAltFallback}
                        widths={widths}
                        className="w-full h-full"
                        imageProps={{ className: 'w-full h-full object-cover object-center' }}
                        loading="lazy"
                    />
                </button>
            ))}
        </div>
    );
}
